import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {HelpGlossaryService} from "../../service/api/help-glossary.service";
import {HelpGlossaryTermDto} from "../../model/dto/help-glossary.dto";

/**
 * Glossary panel component.
 * Displays module-specific glossary terms with search functionality.
 */
@Component({
    selector: 'mvs-glossary-panel',
    templateUrl: './glossary-panel.component.html',
    styleUrls: ['./glossary-panel.component.scss'],
    standalone: false
})
export class GlossaryPanelComponent implements OnInit, OnChanges, OnDestroy {

    @Input() moduleAlias: string;

    initialized = false;
    terms: HelpGlossaryTermDto[] = [];
    filteredTerms: HelpGlossaryTermDto[] = [];
    searchQuery = '';
    selectedLetter: string = null;
    isLoading = true;

    alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

    private subscriptions: Subscription[] = [];

    constructor(private glossaryService: HelpGlossaryService) {}

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize Component.
     */
    initComponent(): void {
        this.loadTerms();
        this.initialized = true;
    }

    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }

        if (changes['moduleAlias']) {
            this.initComponent();
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private loadTerms(): void {
        this.glossaryService.getTerms(this.moduleAlias).subscribe({
            next: terms => {
                this.terms = terms.sort((a, b) => a.term.localeCompare(b.term));
                this.filteredTerms = [...this.terms];
                this.isLoading = false;
            },
            error: err => {
                console.error('Failed to load glossary terms', err);
                this.isLoading = false;
            }
        });
    }

    onSearch(): void {
        this.selectedLetter = null;
        if (!this.searchQuery.trim()) {
            this.filteredTerms = [...this.terms];
            return;
        }

        const query = this.searchQuery.toLowerCase();
        this.filteredTerms = this.terms.filter(t =>
            t.term.toLowerCase().includes(query) ||
            t.definition.toLowerCase().includes(query)
        );
    }

    onLetterClick(letter: string): void {
        this.searchQuery = '';
        if (this.selectedLetter === letter) {
            this.selectedLetter = null;
            this.filteredTerms = [...this.terms];
            return;
        }

        this.selectedLetter = letter;
        this.filteredTerms = this.terms.filter(t =>
            t.term.toUpperCase().startsWith(letter)
        );
    }

    hasTermsForLetter(letter: string): boolean {
        return this.terms.some(t => t.term.toUpperCase().startsWith(letter));
    }
}
